import { useQuery } from "@tanstack/react-query";
import { CDN_BASE_URL } from "pages/whats-new/config";

// Org whitelist for the SCORM export button, hosted as whitelist/SCORM.json
// in the LibertyFi/whats-new repo and served over the same jsDelivr CDN as
// the What's New content. Editing that file rolls the feature out without a
// frontend deploy (jsDelivr caches @main refs, so allow up to ~12h to
// propagate).
const SCORM_CONFIG_URL = `${CDN_BASE_URL}/whitelist/SCORM.json`;

const STALE_TIME = 5 * 60 * 1000;

type ScormRemoteConfig = {
  enabledOrganizationIds?: string[];
};

// `no-cache` revalidates rather than sitting on jsDelivr's week-long
// `max-age` — see useTeacherOnboardingWhitelist for the full reasoning.
const fetchScormExportOrgIds = async (
  signal?: AbortSignal
): Promise<string[]> => {
  const res = await fetch(SCORM_CONFIG_URL, { signal, cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`Failed to load SCORM export config (${res.status})`);
  }
  const json = (await res.json()) as ScormRemoteConfig;
  return json.enabledOrganizationIds ?? [];
};

/**
 * Resolves to [] while loading or on error, so non-admins simply don't see
 * the SCORM button until the config arrives.
 */
export const useScormExportOrgIds = (): string[] => {
  const { data } = useQuery({
    queryKey: ["scorm-export", "org-whitelist"],
    queryFn: ({ signal }) => fetchScormExportOrgIds(signal),
    staleTime: STALE_TIME,
    retry: 1,
  });
  return data ?? [];
};
